import { CH_TYPES, FIXED_TYPES, getDescription, provincesTaxRates, safeParse } from "./quote-utils";
import { useState, useEffect, useCallback } from "react";

export const useQuoteCalculator = (proposal, customPercentages) => {
    const [totalPremiumInsurance, setTotalPremiumInsurance] = useState(0);
    const [employeeTotals, setEmployeeTotals] = useState({});
    const [accountEstimates, setAccountEstimates] = useState({});
    const [planAccountBudget, setPlanAccountBudget] = useState({});
    const [bencatTables, setBencatTables] = useState({});
    const [hasCoreHealth, setHasCoreHealth] = useState(false);
    const [headerBody, setHeaderBody] = useState([]);

    const generateEstimateHeaders = useCallback((accountType, pcts) => {
        const pHeaders = pcts.map((percent) => ({
            text: `${(percent * 100).toFixed(0)}%`,
            alignment: "right",
        }));
        return [{ text: `% ${getDescription(accountType)} Used`, bold: true }, ...pHeaders];
    }, []);

    const generateRow = useCallback((label, values) =>
        [label, ...values.map((value) => ({ text: `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, alignment: "right" }))],
        []);

    const calculateEstimateValues = useCallback((baseValue, percentages) =>
        percentages.map((percent) => {
            const value = baseValue * percent;
            return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }),
        []);

    const calculateAdminFee = useCallback((estValue, adminFee, percentages) =>
        percentages.map(
            (p) => (estValue.single * p + estValue.couple * p + estValue.family * p) * (adminFee / 100)
        ),
        []);

    const calculateTax = useCallback((adminFees, province) =>
        adminFees.map((fee) => fee * provincesTaxRates[province]),
        []);

    const calculateAdditionalONTax = useCallback((estimates, adminFees, percentages) =>
        percentages.map((p, idx) => {
            const total = estimates.single * p + estimates.couple * p + estimates.family * p;
            return total * 0.08 + (total + adminFees[idx]) * 0.02;
        }),
        []);

    const calculateTotalWithGSTAndONTax = useCallback((estimates, adminFees, taxValues, onTaxValues, percentages) =>
        percentages.map(
            (p, idx) =>
                estimates.single * p + estimates.couple * p + estimates.family * p +
                adminFees[idx] + taxValues[idx] +
                (onTaxValues ? onTaxValues[idx] : 0)
        ),
        []);

    // Calculate account totals from proposal data
    const calculateAccountTotals = useCallback(() => {
        if (!proposal?.planAccount) return null;

        const planAccount = JSON.parse(proposal.planAccount);
        const newBencatTables = {};
        const newAccountEstimates = {};
        const newEmployeeTotals = {};
        const proposalNoOfEmployees = { single: 0, couple: 0, family: 0 };
        let newTotalPremiumInsurance = 0;

        const hasCH = planAccount.some(account => CH_TYPES.includes(account.accountType));
        setHasCoreHealth(hasCH);

        planAccount.forEach((acct) => {
            const isFixed = FIXED_TYPES.includes(acct.accountType);
            newBencatTables[acct.accountType] = [
                [
                    { text: 'Job Description', bold: true },
                    { text: 'Single', bold: true },
                    { text: 'Couple', bold: true },
                    { text: 'Family', bold: true },
                    // If the condition is false, the empty array is spread into nothing
                    ...(CH_TYPES.includes(acct.accountType)
                        ? [{ text: 'Excluded from CoreHealth', bold: true }]
                        : [])
                ]
            ];

            let totalSingle = 0;
            let totalCouple = 0;
            let totalFamily = 0;
            let employeeClassTotals = { single: 0, couple: 0, family: 0 };
            let chEmployeeTotals = { single: 0, couple: 0, family: 0 };

            acct?.benefitCategories.forEach((cat) => {
                employeeClassTotals.single += isFixed ?
                    cat.limits.single.noOfEmployees * (parseFloat(cat.limits.single.limitPerEmployee || 0) + parseFloat(cat.limits.single.limitPerEmployeeLSA || 0)) :
                    cat.limits.single.noOfEmployees * parseFloat(cat.limits.single.limitPerEmployee || 0);

                employeeClassTotals.couple += isFixed ?
                    cat.limits.couple.noOfEmployees * (parseFloat(cat.limits.couple.limitPerEmployee || 0) + parseFloat(cat.limits.couple.limitPerEmployeeLSA || 0)) :
                    cat.limits.couple.noOfEmployees * parseFloat(cat.limits.couple.limitPerEmployee || 0);

                employeeClassTotals.family += isFixed ?
                    cat.limits.family.noOfEmployees * (parseFloat(cat.limits.family.limitPerEmployee || 0) + parseFloat(cat.limits.family.limitPerEmployeeLSA || 0)) :
                    cat.limits.family.noOfEmployees * parseFloat(cat.limits.family.limitPerEmployee || 0);

                newBencatTables[acct.accountType].push([
                    cat.description === 'Other' ? cat.customCategory : cat.description,
                    `${isFixed ?
                        "HSA: $" + (cat.limits.single.limitPerEmployee || 0) + ", LSA: $" + (cat.limits.single.limitPerEmployeeLSA || 0) :
                        "$" + (cat.limits.single.limitPerEmployee || 0)}`,
                    `${isFixed ?
                        "HSA: $" + (cat.limits.couple.limitPerEmployee || 0) + ", LSA: $" + (cat.limits.couple.limitPerEmployeeLSA || 0) :
                        "$" + (cat.limits.couple.limitPerEmployee || 0)}`,
                    `${isFixed ?
                        "HSA: $" + (cat.limits.family.limitPerEmployee || 0) + ", LSA: $" + (cat.limits.family.limitPerEmployeeLSA || 0) :
                        "$" + (cat.limits.family.limitPerEmployee || 0)}`,
                    // This logic removes the column entirely if the type isn't in CH_TYPES
                    ...(CH_TYPES.includes(acct.accountType) ? [cat.excludeFromCoreHealth ? "Yes" : "No"] : [])
                ]);

                totalSingle += parseInt(cat.limits.single.noOfEmployees, 10) || 0;
                totalCouple += parseInt(cat.limits.couple.noOfEmployees, 10) || 0;
                totalFamily += parseInt(cat.limits.family.noOfEmployees, 10) || 0;

                // For Core Health accounts, only count employees not excluded
                if (CH_TYPES.includes(acct.accountType)) {
                    chEmployeeTotals.single += cat.excludeFromCoreHealth ? 0 : (parseInt(cat.limits.single.noOfEmployees, 10) || 0);
                    chEmployeeTotals.couple += cat.excludeFromCoreHealth ? 0 : (parseInt(cat.limits.couple.noOfEmployees, 10) || 0);
                    chEmployeeTotals.family += cat.excludeFromCoreHealth ? 0 : (parseInt(cat.limits.family.noOfEmployees, 10) || 0);
                }
            });

            newAccountEstimates[acct.accountType] = employeeClassTotals;
            newEmployeeTotals[acct.accountType] = {
                totalSingle,
                totalCouple,
                totalFamily
            };

            // Calculate insurance premium for core health accounts
            if (CH_TYPES.includes(acct.accountType)) {
                const singleRate = 29.99;
                const coupleRate = 39.99;
                const familyRate = 39.99;
                const taxMultiplier = proposal.province === "ON" ? 1.08 : 1;

                newTotalPremiumInsurance = (
                    chEmployeeTotals.single * singleRate * 12 * taxMultiplier +
                    chEmployeeTotals.couple * coupleRate * 12 * taxMultiplier +
                    chEmployeeTotals.family * familyRate * 12 * taxMultiplier
                ).toFixed(2);
            }
        });

        // Calculate total employee class counts
        proposalNoOfEmployees.single = safeParse(newEmployeeTotals, "HSA", "totalSingle") +
            safeParse(newEmployeeTotals, "LSA", "totalSingle") +
            safeParse(newEmployeeTotals, "CoreHealth", "totalSingle") +
            safeParse(newEmployeeTotals, "Flex", "totalSingle") +
            safeParse(newEmployeeTotals, "CHFlex", "totalSingle") +
            safeParse(newEmployeeTotals, "Fixed", "totalSingle") +
            safeParse(newEmployeeTotals, "CHFixed", "totalSingle");

        proposalNoOfEmployees.couple = safeParse(newEmployeeTotals, "HSA", "totalCouple") +
            safeParse(newEmployeeTotals, "LSA", "totalCouple") +
            safeParse(newEmployeeTotals, "CoreHealth", "totalCouple") +
            safeParse(newEmployeeTotals, "Flex", "totalCouple") +
            safeParse(newEmployeeTotals, "CHFlex", "totalCouple") +
            safeParse(newEmployeeTotals, "Fixed", "totalCouple") +
            safeParse(newEmployeeTotals, "CHFixed", "totalCouple");

        proposalNoOfEmployees.family = safeParse(newEmployeeTotals, "HSA", "totalFamily") +
            safeParse(newEmployeeTotals, "LSA", "totalFamily") +
            safeParse(newEmployeeTotals, "CoreHealth", "totalFamily") +
            safeParse(newEmployeeTotals, "Flex", "totalFamily") +
            safeParse(newEmployeeTotals, "CHFlex", "totalFamily") +
            safeParse(newEmployeeTotals, "Fixed", "totalFamily") +
            safeParse(newEmployeeTotals, "CHFixed", "totalFamily");

        const newHeaderBody = [
            [{ text: 'Company:', bold: true }, proposal.clientCompany],
            [{ text: 'Company Type:', bold: true }, proposal.companyType],
            [{ text: 'Single Employees:', bold: true }, proposalNoOfEmployees.single],
            [{ text: 'Couples:', bold: true }, proposalNoOfEmployees.couple],
            [{ text: 'Employees with Family:', bold: true }, proposalNoOfEmployees.family]
        ];

        // Update all state
        setBencatTables(newBencatTables);
        setAccountEstimates(newAccountEstimates);
        setEmployeeTotals(newEmployeeTotals);
        setTotalPremiumInsurance(newTotalPremiumInsurance);
        setHeaderBody(newHeaderBody);

        return {
            bencatTables: newBencatTables,
            accountEstimates: newAccountEstimates,
            accountTotals: newEmployeeTotals,
            headerBody: newHeaderBody,
            totalPremiumInsurance: newTotalPremiumInsurance
        };
    }, [proposal]);

    // Run calculation whenever proposal changes
    useEffect(() => {
        calculateAccountTotals();
    }, [calculateAccountTotals]);

    const generateCostEstimatesTable = useCallback((accountType) => {
        let percentages = [0.4, 0.65, 0.8, 1.0];
        percentages.push((customPercentages?.[accountType] ?? 0) / 100);

        const adminFees = calculateAdminFee(accountEstimates[accountType], proposal.adminFee, percentages);
        const taxValues = calculateTax(adminFees, proposal.province);
        const onTaxValues =
            proposal.province === "ON" && ["HSA", "Fixed", ...CH_TYPES].includes(accountType)
                ? calculateAdditionalONTax(accountEstimates[accountType], adminFees, percentages)
                : null;

        let totalValues = calculateTotalWithGSTAndONTax(
            accountEstimates[accountType],
            adminFees,
            taxValues,
            onTaxValues,
            percentages
        );

        const rows = [
            generateEstimateHeaders(accountType, percentages),
            generateRow(
                { text: `${getDescription(accountType)} $ Spend (Single)`, bold: true },
                calculateEstimateValues(accountEstimates[accountType].single, percentages)
            ),
            generateRow(
                { text: `${getDescription(accountType)} $ Spend (Couple)`, bold: true },
                calculateEstimateValues(accountEstimates[accountType].couple, percentages)
            ),
            generateRow(
                { text: `${getDescription(accountType)} $ Spend (Family)`, bold: true },
                calculateEstimateValues(accountEstimates[accountType].family, percentages)
            ),
        ];

        // Add insurance premium row for core health accounts
        if (CH_TYPES.includes(accountType)) {
            const formattedPremium = `$${Number(totalPremiumInsurance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

            rows.push([
                { text: `Total Core Insurance Premium${proposal.province === "ON" ? "*" : ""}`, bold: true },
                ...percentages.map(() => ({ text: formattedPremium, alignment: "right" }))
            ]);

            totalValues = totalValues.map(val => parseFloat(val) + parseFloat(totalPremiumInsurance));
        }

        rows.push(generateRow({ text: `${getDescription(accountType)} Admin Fee`, bold: true }, adminFees));
        rows.push(generateRow(
            { text: `GST/HST${proposal.province === "ON" && CH_TYPES.includes(accountType) ? "**" : "*"}`, bold: true },
            taxValues
        ));

        if (proposal.province === "ON" && onTaxValues) {
            rows.push(generateRow({ text: `Additional ON Tax${CH_TYPES.includes(accountType) ? "***" : "**"}`, bold: true }, onTaxValues));
        }

        rows.push([
            { text: "Total", bold: true },
            ...totalValues.map((val) => ({ text: `$${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, alignment: "right", bold: true })),
        ]);

        const updatedBudget = {
            ...planAccountBudget,
            [accountType]: totalValues[3]
        };

        setPlanAccountBudget(updatedBudget);

        return { rows: rows, planAccountBudget: updatedBudget };
    }, [
        accountEstimates,
        proposal,
        customPercentages,
        totalPremiumInsurance,
        planAccountBudget,
        calculateAdminFee,
        calculateTax,
        calculateAdditionalONTax,
        calculateTotalWithGSTAndONTax,
        generateEstimateHeaders,
        generateRow,
        calculateEstimateValues
    ]);

    return {
        accountTotals: employeeTotals,
        headerBody,
        bencatTables,
        hasCoreHealth,
        generateCostEstimatesTable,
        calculateAccountTotals
    };
};